import _ from 'underscore';
import React, { useCallback, useEffect, useState, useRef, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { useOnceEffect } from './helper';
import { WIZARD_EVENT_PREFIX, WIZARD_STEP_EVENT } from './constants';

const NoOpContext = {
  logComplete: _.noop,
  changedChannel: _.noop,
  enteredPage: _.noop,
};

export const StepFlowContext = React.createContext(NoOpContext);
const DEFAULT_EVENT_VALUE = '';

export function useLogEnteredPage({
  Flow,
  SubFlow = DEFAULT_EVENT_VALUE,
  Page,
}) {
  const { enteredPage } = useContext(StepFlowContext);
  useEffect(() => {
    if (Page) {
      enteredPage({ Flow, SubFlow, Page });
    }
  }, [Flow, Page, SubFlow, enteredPage]);
}

function useStateWithPrevious(initialValue) {
  const [state, setState] = useState(initialValue);
  const cache = useRef({});

  const updateState = useCallback((newValue) => {
    const value = {
      Flow: newValue.Flow || DEFAULT_EVENT_VALUE,
      SubFlow: newValue.SubFlow || DEFAULT_EVENT_VALUE,
      Page: newValue.Page || DEFAULT_EVENT_VALUE,
    };
    setState((prev) => {
      if (!_.isEqual(prev, value)) {
        cache.current = prev;
        return value;
      }
      return prev;
    });
  }, []);

  return [state, updateState, cache.current];
}

export function StepFlowProvider({ children, shouldPost, postEventLog }) {
  const serverContext = useContext(ServerContext);
  const instrumentation = useContext(InstrumentationContext);

  const activity = useMemo(() => {
    if (instrumentation && instrumentation.stepFlowScenario) {
      const scenario = instrumentation.stepFlowScenario.create();
      return scenario.stepFlow.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const triggerPostEventLog = postEventLog(serverContext, activity.trace);
  const shouldPostLog = shouldPost && _.isFunction(activity.trace) && _.isFunction(triggerPostEventLog);

  const [{
    Flow,
    SubFlow,
    Page,
  }, setContext, prevContext] = useStateWithPrevious({
    Flow: DEFAULT_EVENT_VALUE,
    SubFlow: DEFAULT_EVENT_VALUE,
    Page: DEFAULT_EVENT_VALUE,
  });

  const eventValues = useRef({});
  const postEventLogRef = useRef({});
  postEventLogRef.current = {
    postEvent: (EventName, EventValue) => triggerPostEventLog({
      EventName: `${WIZARD_EVENT_PREFIX}${EventName}`,
      EventValue,
    }),
    logValidationError: (error) => {
      const message = `WizardStepFlow validation error: ${error}`;
      activity.trace(message);
    },
  };

  useOnceEffect(() => {
    if (shouldPostLog) {
      postEventLogRef.current.postEvent(WIZARD_STEP_EVENT.APP_LOAD, {
        ...eventValues.current,
      });
      return true;
    }
    return false;
  });

  useOnceEffect(() => {
    if (shouldPostLog && Flow !== DEFAULT_EVENT_VALUE) {
      postEventLogRef.current.postEvent(WIZARD_STEP_EVENT.START_FLOW, {
        ...eventValues.current,
        Flow,
      });
      return true;
    }
    return false;
  });

  const logComplete = useCallback(() => {
    postEventLogRef.current.postEvent(WIZARD_STEP_EVENT.COMPLETE, {
      ...eventValues.current,
      Flow,
      SubFlow,
      Page,
      Complete: true,
    });
  }, [Flow, SubFlow, Page]);


  useEffect(() => {
    if (!shouldPost || Flow === DEFAULT_EVENT_VALUE) {
      return;
    }

    if (
      (prevContext.Flow !== Flow || prevContext.SubFlow !== SubFlow || prevContext.Page !== Page) &&
      Page !== DEFAULT_EVENT_VALUE
    ) {
      postEventLogRef.current.postEvent(WIZARD_STEP_EVENT.ENTER_PAGE, {
        ...eventValues.current,
        Flow,
        SubFlow,
        Page,
      });
    }

    if (prevContext.Flow !== Flow && prevContext.Flow !== DEFAULT_EVENT_VALUE) {
      if (prevContext.Page === Page) {
        postEventLogRef.current.logValidationError('page should have been modified when flow changed');
      }

      postEventLogRef.current.postEvent(WIZARD_STEP_EVENT.SWITCH_FLOW, {
        CID: eventValues.current.CID,
        AID: eventValues.current.AID,
        FromFlow: prevContext.Flow,
        ToFlow: Flow,
        FromPage: prevContext.Page || null,
        ToPage: Page || null,
      });
    }
  }, [Flow, Page, SubFlow, prevContext.Flow, prevContext.Page, prevContext.SubFlow, shouldPost]);

  return (
    <StepFlowContext.Provider value={shouldPost ? {
      enteredPage: setContext,
      logComplete,
    } : NoOpContext}
    >
      {children}
    </StepFlowContext.Provider>
  );
}

StepFlowProvider.propTypes = {
  shouldPost: PropTypes.bool,
  postEventLog: PropTypes.func.isRequired,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
  serverContext: PropTypes.shape({}),
};

StepFlowProvider.defaultProps = {
  shouldPost: true,
  serverContext: undefined,
};
